import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Plus, Trash2, Globe, Users, Settings, Palette, UserPlus, Brain, Plug } from "lucide-react";
import { InviteUserDialog } from "@/components/InviteUserDialog";
import { toast } from "sonner";
import { format } from "date-fns";


interface Organization {
  id: string;
  name: string;
  slug: string;
  is_active: boolean;
  is_flagship: boolean;
  logo_url: string | null;
  created_at: string;
}

interface Domain {
  id: string;
  domain: string;
  is_primary: boolean;
  is_verified: boolean;
  created_at: string;
}

interface OrgUser {
  id: string;
  email: string | null;
  approved: boolean | null;
  created_at: string | null;
  roles: string[];
}

interface OrgSettings {
  id: string;
  site_title: string | null;
  site_description: string | null;
  primary_color: string | null;
  secondary_color: string | null;
  background_color: string | null;
  foreground_color: string | null;
  font_heading: string | null;
  font_body: string | null;
  enable_sandbox: boolean | null;
  enable_library: boolean | null;
  enable_events: boolean | null;
  enable_ai_chat: boolean | null;
  enable_integrations: boolean | null;
  require_auth: boolean | null;
  contact_email: string | null;
  ai_system_instructions: string | null;
  ai_context_provider: string | null;
  // Integration overrides
  stripe_publishable_key: string | null;
  openai_api_key: string | null;
  zapier_webhook_url: string | null;
  gtm_snippet: string | null;
  contact_notification_emails: string | null;
  payment_notification_emails: string | null;
}

const OrganizationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const [newDomain, setNewDomain] = useState({ domain: '', is_primary: false });
  const [isAddDomainOpen, setIsAddDomainOpen] = useState(false);

  // Fetch organization
  const { data: org, isLoading: orgLoading } = useQuery({
    queryKey: ['organization', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', id)
        .single();
      if (error) throw error;
      return data as Organization;
    },
    enabled: !!id
  });

  // Fetch domains
  const { data: domains, isLoading: domainsLoading } = useQuery({
    queryKey: ['organization-domains', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('organization_domains')
        .select('*')
        .eq('organization_id', id)
        .order('is_primary', { ascending: false });
      if (error) throw error;
      return data as Domain[];
    },
    enabled: !!id
  });

  // Fetch users
  const { data: users, isLoading: usersLoading } = useQuery({
    queryKey: ['organization-users', id],
    queryFn: async () => {
      const { data: profiles, error } = await supabase
        .from('profiles')
        .select('id, email, approved, created_at')
        .eq('organization_id', id);
      if (error) throw error;

      // Get roles for each user
      const userIds = profiles.map(p => p.id);
      const { data: rolesData } = await supabase
        .from('user_roles')
        .select('user_id, role')
        .in('user_id', userIds);

      const roleMap = new Map<string, string[]>();
      rolesData?.forEach(r => {
        const existing = roleMap.get(r.user_id) || [];
        existing.push(r.role);
        roleMap.set(r.user_id, existing);
      });

      return profiles.map(p => ({
        ...p,
        roles: roleMap.get(p.id) || []
      })) as OrgUser[];
    },
    enabled: !!id
  });

  // Fetch settings
  const { data: settings, isLoading: settingsLoading } = useQuery({
    queryKey: ['organization-settings', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('organization_settings')
        .select('*')
        .eq('organization_id', id)
        .single();
      if (error) throw error;
      return data as OrgSettings;
    },
    enabled: !!id
  });

  // Add domain mutation
  const addDomainMutation = useMutation({
    mutationFn: async (data: { domain: string; is_primary: boolean }) => {
      const { error } = await supabase
        .from('organization_domains')
        .insert({
          organization_id: id,
          domain: data.domain,
          is_primary: data.is_primary,
        });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization-domains', id] });
      setIsAddDomainOpen(false);
      setNewDomain({ domain: '', is_primary: false });
      toast.success('Domain added successfully');
    },
    onError: (error) => {
      toast.error(`Failed to add domain: ${error.message}`);
    }
  });

  // Delete domain mutation
  const deleteDomainMutation = useMutation({
    mutationFn: async (domainId: string) => {
      const { error } = await supabase
        .from('organization_domains')
        .delete()
        .eq('id', domainId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization-domains', id] });
      toast.success('Domain removed');
    },
    onError: (error) => {
      toast.error(`Failed to remove domain: ${error.message}`);
    }
  });

  // Update settings mutation
  const updateSettingsMutation = useMutation({
    mutationFn: async (data: Partial<OrgSettings>) => {
      const { error } = await supabase
        .from('organization_settings')
        .update(data)
        .eq('organization_id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization-settings', id] });
      toast.success('Settings updated');
    },
    onError: (error) => {
      toast.error(`Failed to update settings: ${error.message}`);
    }
  });

  if (orgLoading) {
    return (
      <div className="p-6">
        <div className="text-muted-foreground">Loading organization...</div>
      </div>
    );
  }

  if (!org) {
    return (
      <div className="p-6">
        <div className="text-destructive">Organization not found</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/super-admin/organizations')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold font-display">{org.name}</h1>
              {org.is_flagship && <Badge variant="secondary">Flagship</Badge>}
              <Badge variant={org.is_active ? "default" : "outline"}>
                {org.is_active ? 'Active' : 'Inactive'}
              </Badge>
            </div>
            <p className="text-muted-foreground">/{org.slug}</p>
          </div>
        </div>
      </div>

      <Tabs defaultValue="general" className="space-y-6">
        <TabsList>
          <TabsTrigger value="general" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            General
          </TabsTrigger>
          <TabsTrigger value="ai" className="flex items-center gap-2">
            <Brain className="h-4 w-4" />
            AI
          </TabsTrigger>
          <TabsTrigger value="domains" className="flex items-center gap-2">
            <Globe className="h-4 w-4" />
            Domains
          </TabsTrigger>
          <TabsTrigger value="users" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Users
          </TabsTrigger>
          <TabsTrigger value="branding" className="flex items-center gap-2">
            <Palette className="h-4 w-4" />
            Branding
          </TabsTrigger>
          <TabsTrigger value="integrations" className="flex items-center gap-2">
            <Plug className="h-4 w-4" />
            Integrations
          </TabsTrigger>
        </TabsList>

        {/* General Tab */}
        <TabsContent value="general">
          <Card>
            <CardHeader>
              <CardTitle>General Settings</CardTitle>
              <CardDescription>Basic organization configuration</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Site Title</Label>
                  <Input 
                    value={settings?.site_title || ''} 
                    onChange={(e) => updateSettingsMutation.mutate({ site_title: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Contact Email</Label>
                  <Input 
                    type="email"
                    value={settings?.contact_email || ''} 
                    onChange={(e) => updateSettingsMutation.mutate({ contact_email: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Site Description</Label>
                <Input 
                  value={settings?.site_description || ''} 
                  onChange={(e) => updateSettingsMutation.mutate({ site_description: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4 pt-4">
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <Label>Require Authentication</Label>
                  <Switch 
                    checked={settings?.require_auth || false}
                    onCheckedChange={(checked) => updateSettingsMutation.mutate({ require_auth: checked })}
                  />
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <Label>Enable Sandbox</Label>
                  <Switch 
                    checked={settings?.enable_sandbox ?? true}
                    onCheckedChange={(checked) => updateSettingsMutation.mutate({ enable_sandbox: checked })}
                  />
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <Label>Enable Library</Label>
                  <Switch 
                    checked={settings?.enable_library ?? true}
                    onCheckedChange={(checked) => updateSettingsMutation.mutate({ enable_library: checked })}
                  />
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <Label>Enable Events</Label>
                  <Switch 
                    checked={settings?.enable_events ?? true}
                    onCheckedChange={(checked) => updateSettingsMutation.mutate({ enable_events: checked })}
                  />
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <Label>Enable AI Chat</Label>
                  <Switch 
                    checked={settings?.enable_ai_chat ?? true}
                    onCheckedChange={(checked) => updateSettingsMutation.mutate({ enable_ai_chat: checked })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Tab */}
        <TabsContent value="ai">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Brain className="h-5 w-5" />
                AI Configuration
              </CardTitle>
              <CardDescription>Configure AI behavior across all features for this organization</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Site-Wide AI Instructions</Label>
                <Textarea 
                  value={settings?.ai_system_instructions || ''} 
                  onChange={(e) => updateSettingsMutation.mutate({ ai_system_instructions: e.target.value })}
                  placeholder="e.g., 'When responding to questions about faith and morals, respond from a Catholic worldview...'"
                  rows={4}
                />
                <p className="text-xs text-muted-foreground">
                  These instructions are applied to ALL AI prompts (AI Companion, Library Chat, Planner)
                </p>
              </div>

              <div className="space-y-2">
                <Label>AI Context Provider for Library Chat</Label>
                <Select 
                  value={settings?.ai_context_provider || 'lovable'} 
                  onValueChange={(value) => updateSettingsMutation.mutate({ ai_context_provider: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Library Only (No Additional AI Context)</SelectItem>
                    <SelectItem value="lovable">Lovable AI (Gemini - General Context)</SelectItem>
                    <SelectItem value="magisterium">Magisterium (Theological Context)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Controls what AI provides additional context beyond library resources. Magisterium requires API key configuration.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Domains Tab */}
        <TabsContent value="domains">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Domains</CardTitle>
                <CardDescription>Manage domains that route to this organization</CardDescription>
              </div>
              <Dialog open={isAddDomainOpen} onOpenChange={setIsAddDomainOpen}>
                <DialogTrigger asChild>
                  <Button size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Domain
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Domain</DialogTitle>
                    <DialogDescription>Add a new domain that routes to this organization</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Domain</Label>
                      <Input
                        value={newDomain.domain}
                        onChange={(e) => setNewDomain({ ...newDomain, domain: e.target.value })}
                        placeholder="example.com or subdomain.example.com"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>Primary Domain</Label>
                      <Switch
                        checked={newDomain.is_primary}
                        onCheckedChange={(checked) => setNewDomain({ ...newDomain, is_primary: checked })}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setIsAddDomainOpen(false)}>Cancel</Button>
                    <Button onClick={() => addDomainMutation.mutate(newDomain)} disabled={addDomainMutation.isPending}>
                      {addDomainMutation.isPending ? 'Adding...' : 'Add Domain'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {domainsLoading ? (
                <div className="text-muted-foreground py-4">Loading domains...</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Domain</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Added</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {domains?.map((domain) => (
                      <TableRow key={domain.id}>
                        <TableCell className="font-medium">
                          {domain.domain}
                          {domain.is_primary && <Badge variant="secondary" className="ml-2">Primary</Badge>}
                        </TableCell>
                        <TableCell>
                          <Badge variant={domain.is_verified ? "default" : "outline"}>
                            {domain.is_verified ? 'Verified' : 'Pending'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {format(new Date(domain.created_at), 'MMM d, yyyy')}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => deleteDomainMutation.mutate(domain.id)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {domains?.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                          No domains configured
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Users</CardTitle>
                <CardDescription>Users assigned to this organization</CardDescription>
              </div>
              <InviteUserDialog 
                organizationId={id!}
                organizationName={org?.name}
                showSuperAdminOption={true}
                onSuccess={() => queryClient.invalidateQueries({ queryKey: ['organization-users', id] })}
              />
            </CardHeader>
            <CardContent>
              {usersLoading ? (
                <div className="text-muted-foreground py-4">Loading users...</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Roles</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Joined</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users?.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">{user.email}</TableCell>
                        <TableCell>
                          <div className="flex gap-1 flex-wrap">
                            {user.roles.length > 0 ? user.roles.map(role => (
                              <Badge key={role} variant="secondary">{role}</Badge>
                            )) : (
                              <span className="text-muted-foreground text-sm">No roles</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={user.approved ? "default" : "outline"}>
                            {user.approved ? 'Approved' : 'Pending'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {user.created_at ? format(new Date(user.created_at), 'MMM d, yyyy') : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                    {users?.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                          No users in this organization
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Branding Tab */}
        <TabsContent value="branding">
          <Card>
            <CardHeader>
              <CardTitle>Branding & Theming</CardTitle>
              <CardDescription>Customize the look and feel for this organization</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Primary Color (HSL)</Label>
                  <Input 
                    value={settings?.primary_color || ''} 
                    onChange={(e) => updateSettingsMutation.mutate({ primary_color: e.target.value })}
                    placeholder="199 89% 32%"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Secondary Color (HSL)</Label>
                  <Input 
                    value={settings?.secondary_color || ''} 
                    onChange={(e) => updateSettingsMutation.mutate({ secondary_color: e.target.value })}
                    placeholder="160 84% 39%"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Background Color (HSL)</Label>
                  <Input 
                    value={settings?.background_color || ''} 
                    onChange={(e) => updateSettingsMutation.mutate({ background_color: e.target.value })}
                    placeholder="222 47% 11%"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Foreground Color (HSL)</Label>
                  <Input 
                    value={settings?.foreground_color || ''} 
                    onChange={(e) => updateSettingsMutation.mutate({ foreground_color: e.target.value })}
                    placeholder="210 40% 98%"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Heading Font</Label>
                  <Input 
                    value={settings?.font_heading || ''} 
                    onChange={(e) => updateSettingsMutation.mutate({ font_heading: e.target.value })}
                    placeholder="Space Grotesk"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Body Font</Label>
                  <Input 
                    value={settings?.font_body || ''} 
                    onChange={(e) => updateSettingsMutation.mutate({ font_body: e.target.value })}
                    placeholder="Inter"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Integrations Tab */}
        <TabsContent value="integrations">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plug className="h-5 w-5" />
                Integration Settings
              </CardTitle>
              <CardDescription>
                Enable client admin access to integrations and configure organization-specific API key overrides.
                When set, these values take precedence over platform defaults for this organization only.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Enable Integrations Toggle */}
              <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
                <div>
                  <Label className="text-base font-medium">Enable Integrations for Client Admins</Label>
                  <p className="text-sm text-muted-foreground">
                    When enabled, organization admins can view and modify integration settings in their admin panel.
                  </p>
                </div>
                <Switch 
                  checked={settings?.enable_integrations || false}
                  onCheckedChange={(checked) => updateSettingsMutation.mutate({ enable_integrations: checked })}
                />
              </div>

              {/* Organization-Specific Overrides */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span className="font-medium">Organization-Specific Overrides</span>
                  <span className="text-xs bg-muted px-2 py-0.5 rounded">Leave blank to use platform defaults</span>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Stripe Publishable Key</Label>
                    <Input 
                      value={settings?.stripe_publishable_key || ''} 
                      onChange={(e) => updateSettingsMutation.mutate({ stripe_publishable_key: e.target.value })}
                      placeholder="pk_live_... (uses platform default if blank)"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>OpenAI API Key</Label>
                    <Input 
                      type="password"
                      value={settings?.openai_api_key || ''} 
                      onChange={(e) => updateSettingsMutation.mutate({ openai_api_key: e.target.value })}
                      placeholder="sk-... (uses platform default if blank)"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Zapier Registration Webhook URL</Label>
                  <Input 
                    value={settings?.zapier_webhook_url || ''} 
                    onChange={(e) => updateSettingsMutation.mutate({ zapier_webhook_url: e.target.value })}
                    placeholder="https://hooks.zapier.com/... (uses platform default if blank)"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Contact Form Notification Emails</Label>
                    <Input 
                      value={settings?.contact_notification_emails || ''} 
                      onChange={(e) => updateSettingsMutation.mutate({ contact_notification_emails: e.target.value })}
                      placeholder="email1@example.com, email2@example.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Payment Notification Emails</Label>
                    <Input 
                      value={settings?.payment_notification_emails || ''} 
                      onChange={(e) => updateSettingsMutation.mutate({ payment_notification_emails: e.target.value })}
                      placeholder="finance@example.com"
                    />
                  </div>
                </div>

              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default OrganizationDetail;
